import { useState, useEffect, useRef } from 'react';
import Modal from "@modalSrc/Modal";
import HelpModal from "@modals/HelpModal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { IoAddOutline } from 'react-icons/io5';
import { FaPlusSquare, FaQuestionCircle, FaFileDownload, FaFileUpload } from "react-icons/fa";
import { DateTime } from 'luxon';
import Helpers from "@/Helpers";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import { downloadAuthenticatedFile } from "@/utils/authenticatedFileDownload";
import Enums from "@/enums";

type JobDescriptionRecord = {
    id?: number;
    jobId?: number;
    description?: string;
    stateId?: number;
    notes?: string;
    attachmentId?: number;
    attachmentFileName?: string;
    [key: string]: unknown;
};

export default function JobDescriptionModal({
    jobId,
    jobDescriptionId,
    onClose,
    className = '',
    updateSpinner
}) {
    const [job, setJob] = useState(null);
    const [descriptionHistory, setDescriptionHistory] = useState([]);
    const [selectedDescription, setSelectedDescription] = useState<JobDescriptionRecord>({});
    const [loading, setLoading] = useState(true);
    const [statuses, setStatuses] = useState([]); // Store available statuses
    const [showHelpPanel, setShowHelpPanel] = useState(false); // Toggle help panel
    const [selectedFile, setSelectedFile] = useState(null); // Store selected file
    const [isDragOver, setIsDragOver] = useState(false); // Track drag-over state
    const textareaRefs = useRef([]);
    const descriptionTextareaRef = useRef(null);

    const fetchJobData = async () => {
        if (!jobId) return;

        setLoading(true);
        if (updateSpinner) updateSpinner(true);

        try {
            // Fetch job data
            const jobResp = await RightTimeApi.getAsync(`/Job/${jobId}`);

            // Handle job data response
            if (jobResp && jobResp.ok) {
                setJob(jobResp.jsonData);
                const history = jobResp.jsonData.descriptionHistory || [];
                setDescriptionHistory(history);

                if (jobDescriptionId && jobDescriptionId > 0) {
                    // Load specific description
                    const desc = history.find(d => d.id === jobDescriptionId);
                    if (desc) {
                        setSelectedDescription(desc);
                    } else {
                        toast.error("Job description not found.");
                        setSelectedDescription({
                            jobId: jobResp.jsonData.id,
                            description: '',
                            stateId: Enums.JOB_DESCRIPTION_STATE.PendingDescription
                        });
                    }
                } else {
                    // Initialize for new description
                    setSelectedDescription({
                        jobId: jobResp.jsonData.id,
                        description: '',
                        stateId: Enums.JOB_DESCRIPTION_STATE.PendingDescription
                    });
                }
            } else {
                toast.error("Failed to load job data.");
            }
        } catch (error) {
            toast.error("Error loading data.");
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
            if (updateSpinner) updateSpinner(false);
        }
    };

    useEffect(() => {
        fetchJobData();
    }, [jobId, jobDescriptionId, updateSpinner]);

    // Set up ResizeObserver for textareas to ensure proper modal sizing
    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            // Calculate the required width based on textarea content
            let maxWidth = 800; // Start with minimum width

            textareaRefs.current.forEach(textarea => {
                if (textarea) {
                    const textareaWidth = textarea.offsetWidth;
                    const cellPadding = 16; // td padding
                    const labelWidth = 200; // th width
                    const tablePadding = 40; // table margins/padding
                    const modalPadding = 40; // modal body padding

                    const requiredWidth = labelWidth + textareaWidth + cellPadding + tablePadding + modalPadding;
                    maxWidth = Math.max(maxWidth, requiredWidth);
                }
            });

            // Apply the calculated width to the modal
            const modal = document.querySelector('.jobDescriptionModal.modal') as HTMLDivElement | null;
            if (modal) {
                modal.style.width = `${Math.min(maxWidth, window.innerWidth * 0.95)}px`;
            }
        });

        // Use refs for more precise observation
        textareaRefs.current.forEach(textarea => {
            if (textarea) {
                resizeObserver.observe(textarea);
            }
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, [jobId, selectedFile]);

    const handleDescriptionChange = (e) => {
        const nextValue = e.target.value;
        const selectionStart = e.target.selectionStart;
        const selectionEnd = e.target.selectionEnd;

        setSelectedDescription(prev => ({
            ...prev,
            description: nextValue
        }));

        requestAnimationFrame(() => {
            const textarea = descriptionTextareaRef.current;
            if (!textarea) return;
            if (document.activeElement !== textarea) {
                textarea.focus();
            }
            if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
                textarea.setSelectionRange(selectionStart, selectionEnd);
            }
        });
    };

    const handleAddDescription = () => {
        // Clear the selectedDescription object to reset form fields
        // Keep job-related information for reference
        setSelectedDescription({
            jobId: job?.id || jobId,  // Preserve job ID
            description: '',
            notes: '',
            stateId: Enums.JOB_DESCRIPTION_STATE.PendingDescription  // Initialize to PendingDescription
        });

        // Clear selected file as well
        setSelectedFile(null);

        toast.info("Ready to create a new job description.");
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!jobDescriptionId || jobDescriptionId === 0 || jobDescriptionId === null) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (jobDescriptionId && jobDescriptionId > 0) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSelectedFile(file);
            toast.success(`File "${file.name}" ready for upload`);
        }
    };

    const handleRowClick = (historyItem) => {
        // Map the history item properties to the form fields
        setSelectedDescription({
            id: historyItem.id,
            jobId: job?.id || jobId,
            description: historyItem.description || '',
            notes: historyItem.notes || '',
            status: historyItem.stateId || historyItem.status || '',
            version: historyItem.version || '',
            date: historyItem.CreatedTime || historyItem.date,
            createdBy: historyItem.createdBy || historyItem.CreatedBy,
            hasFile: historyItem.hasFile || false,
            // Keep any other properties that might be needed
            ...historyItem
        });
        setSelectedFile(null); // Clear file selection when switching descriptions
    };

    const helpContent = (
        <>
            <div className="helpSection">
                <h5>Status</h5>
                <p>Select the current status of this job description:</p>
                <ul>
                    <li><strong>Draft:</strong> Initial creation, work in progress</li>
                    <li><strong>Pending Review:</strong> Submitted for approval</li>
                    <li><strong>Approved:</strong> Finalized and ready for use</li>
                    <li><strong>Rejected:</strong> Needs revision</li>
                </ul>
            </div>

            <div className="helpSection">
                <h5>Description</h5>
                <p>Enter the complete job description including:</p>
                <ul>
                    <li>Job title and summary</li>
                    <li>Key responsibilities</li>
                    <li>Required qualifications</li>
                    <li>Preferred skills</li>
                    <li>Working conditions</li>
                </ul>
            </div>

            <div className="helpSection">
                <h5>Notes</h5>
                <p>Add any additional notes, comments, or internal information about this job description.</p>
            </div>

            <div className="helpSection">
                <h5>File Upload</h5>
                <p>Optionally upload a document file containing the job description. Supported formats: PDF, DOC, DOCX, TXT.</p>
            </div>
        </>
    );

    const onSave = async () => {
        try {
            if (updateSpinner) updateSpinner(true);

            // If a file is selected, upload it (and only it)
            if (selectedFile) {
                const fileResp = await RightTimeApi.postFileAsync(`/Job/${job.id}/Description/File`, selectedFile);

                if (fileResp && fileResp.ok) {
                    toast.success("Description file saved successfully!");
                } else {
                    toast.error("Failed to save description file.");
                }
            } else if (selectedDescription.description?.trim()) {
                // If textarea has content, save the description
                const payload = {
                    id: selectedDescription.id || 0,
                    companyJobid: job.id,
                    description: selectedDescription.description,
                    stateId: selectedDescription.stateId,
                    companyJobDescriptionId: selectedDescription.id,
                };

                const descResp = await RightTimeApi.sendAsync(`/Job/${job.id}/Description`, payload);

                if (descResp && descResp.ok) {
                    toast.success("Description saved successfully!");
                } else {
                    toast.error("Failed to save description.");
                }
            } else {
                toast.error("Please enter a description or drop a file.");
                if (updateSpinner) updateSpinner(false);
                return;
            }

            // Refresh the job data to show updated history
            await fetchJobData();

            // Clear the form for a new entry
            setSelectedFile(null);

            // Automatically close the modal after successful save
            onClose();
        } catch (error) {
            toast.error("Error saving data.");
            console.error('Error saving:', error);
        } finally {
            if (updateSpinner) updateSpinner(false);
        }
    }

    // Handle file download
    const downloadFile = async (jdId, event) => {
        // Prevent row click when clicking the download icon
        event.stopPropagation();

        if (!jdId) {
            toast.error("No file ID available for download.");
            return;
        }

        try {
            const ok = await downloadAuthenticatedFile(
                `/CompanyJob/${job.id}/Description/${jdId}/File`,
                "download"
            );
            if (ok) toast.success("File downloaded successfully!");
            else toast.error("Error downloading file.");
        } catch (error) {
            toast.error("Error downloading file.");
            console.error('Error downloading file:', error);
        }
    }

    // Copy BE Code URL to clipboard
    const copyToClipboard = async (efCode) => {
        if (!efCode) {
            return;
        }

        const baseURL = Helpers.Env("VITE_TEST_RUNNER_URL");
        const url = baseURL + efCode;

        if (!url || url.length === 0) {
            return;
        }

        try {
            const copied = await copyUrlAsLink(url);
            if (copied) {
                toast.success("Copied URL to clipboard");
            } else {
                toast.error("Could not copy URL to clipboard");
            }
        } catch (err) {
            toast.error("Could not copy URL: " + err);
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setSelectedDescription(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle file selection
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
    };

    // Helper function to get status name from stateId
    const getStatusName = (stateId) => {
        if (!stateId || !statuses || statuses.length === 0) {
            return '-';
        }

        const status = statuses.find(s =>
            s.id === stateId ||
            s.stateId === stateId ||
            s.value === stateId
        );

        return status ? (status.name || status.label || status.value || status) : '-';
    };

    const renderJobDescriptionForm = () => {
        return (<div>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description:</label>
                {selectedFile ? (
                    <div 
                        style={{
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                        onClick={() => setSelectedFile(null)}
                        title="Click to remove file and enter description manually"
                    >
                        <span style={{ fontWeight: '500' }}>{selectedFile.name}</span>
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>Click to change</span>
                    </div>
                ) : (
                    <textarea
                        ref={el => {
                            textareaRefs.current[0] = el;
                            descriptionTextareaRef.current = el;
                        }}
                        placeholder="Enter job description or drop a file here"
                        rows={8}
                        className="form-control"
                        style={{ resize: 'both' }}
                        value={selectedDescription.description || ''}
                        onChange={handleDescriptionChange}
                    ></textarea>
                )}
            </div>
        </div>
        );
    };

    const modalTitle = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
                {job 
                    ? `${!jobDescriptionId || jobDescriptionId === 0 ? 'Add ' : ''}Job Description to ${job.name} (${job.id})` 
                    : 'Job Description'}
            </span>
            <button
                type="button"
                className="btn btn-sm btn-outline-info helpToggleBtn"
                onClick={() => setShowHelpPanel(true)}
                title="Show Help"
                style={{ marginLeft: '10px' }}
            >
                <FaQuestionCircle />
            </button>
        </div>
    );

    const footer = (
        <div className="footerRight">
            <button type="button" className="btn btn-primary me-2" onClick={onSave}>
                Save Description
            </button>
        </div>
    );

    return (
        <>
        <Modal
            isOpen={jobId}
            title={modalTitle}
            onClose={onClose}
            className={`jobDescriptionModal ${className}`}
            updateSpinner={updateSpinner}
            size="lg"
            footer={footer}
        >
            <div 
                className={`jobDescriptionModalContainer ${showHelpPanel ? 'withHelpPanel' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    position: 'relative',
                    minHeight: '100%',
                    border: isDragOver && (!jobDescriptionId || jobDescriptionId === 0) ? '2px dashed #007bff' : '2px dashed transparent',
                    borderRadius: '8px',
                    transition: 'border-color 0.2s ease'
                }}
            >
                {isDragOver && (!jobDescriptionId || jobDescriptionId === 0) && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 123, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            zIndex: 1000,
                            pointerEvents: 'none'
                        }}
                    >
                        <div style={{
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                            textAlign: 'center',
                            border: '2px solid #007bff'
                        }}>
                            <FaFileUpload size={48} color="#007bff" style={{ marginBottom: '10px' }} />
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                                Drop job description file here
                            </div>
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                Any file type accepted
                            </div>
                        </div>
                    </div>
                )}
                <div className="jobDescriptionContent">
                    {/* {renderSelectedDescriptionDetails()} */}
                    {renderJobDescriptionForm()}
                </div>
            </div>
        </Modal>
        
        <HelpModal
            isOpen={showHelpPanel}
            onClose={() => setShowHelpPanel(false)}
            title="Job Description Help"
            content={helpContent}
        />
        </>
    );
}


